(ns livefeed.poll
  (:require [cljs.core.async :refer-macros [go] :refer [<!]]
            [goog.string :as gstring]
            [reagent.core :as reagent :refer [atom with-let]]
            ["rc-slider" :default slider]
            [livefeed.api :as api]
            [livefeed.analytics :as analytics]
            [livefeed.icons :as icons]
            [livefeed.i18n :refer [translate]]
            [livefeed.modal :as modal]
            [livefeed.state :as state]
            [livefeed.tunnus :as tunnus]
            [livefeed.util :as util]
            [livefeed.properties :as properties]
            [livefeed.websocket :as websocket]))

(defonce poll-state (reagent/atom {:poll          nil
                                   :results       nil
                                   :step          nil})) ;; one of [nil :show-poll :show-thanks :show-poll-results]

(defn close-poll-modal! []
  (swap! poll-state assoc :step nil))

(defn close-poll-modal-and-track-event! []
  (close-poll-modal!))

(defn show-results! [data]
  (when (and (not (= :show-poll-results (:step @poll-state)))
             (not (= :show-poll (:step @poll-state))))
    (swap! poll-state assoc
           :step :show-poll-results
           :results data)))

(defn answer-poll! [answer]
  (when-let [poll-id (get-in @poll-state [:poll :poll-id])]
    (websocket/send-poll-answer poll-id answer)
    (js/setTimeout
     #(go
        (let [results (<! (api/get-poll-results poll-id))]
          (if (seq results)
            (swap! poll-state assoc :step :show-poll-results :results results)
            (swap! poll-state assoc :step :show-thanks))))
     250)))

(defn new-poll? [old-poll current-poll]
  (not= (:poll-id old-poll) (:poll-id current-poll)))

(defn poll-open? [poll]
  (and poll
       (:is-opened poll)
       (not (:is-closed poll))))

(defn update-poll! [poll]
  (let [open? (poll-open? poll)
        answered? (:has-answered? poll)
        is-current? (not (new-poll? (:poll @poll-state) poll))]
    (cond
      (and open? (not answered?) (not is-current?)) (do
                                                      (swap! poll-state assoc :poll poll :step :show-poll))
      (and (not open?) is-current?) (close-poll-modal!)
      (and open? answered? is-current?) (close-poll-modal!)
      :else nil)))

(def ^:private slider-common-props
  {:class-name :poll-modal__slider__container
   :min        1
   :max        100
   :step       1
   :rail-style {:background-color "#f1f2f4"
                :height 4}
   :track-style {:background-color "#f1f2f4"
                 :height 4}})

(defn- slider-handle [emoji]
  (fn [^js props]
    (reagent/as-element [:div {:class [:poll-modal__slider__handle
                                       (when (.-disabled props)
                                         :poll-modal__slider__handle--disabled)]
                               :ref   (.-ref props)
                               :style {:left (str (.-value props) "%")}}
                         emoji])))

(defn poll-answering [poll]
  (with-let [slider-value (atom 50)
             loading (atom false)]
    (let [{:keys [poll-id title options]} poll
          on-answer-poll! #(do
                             (reset! loading true)
                             (answer-poll! %))
          options (into [:div.poll-modal__options.poll-modal__scroll-container]
                        (for [[index option] (util/indexed options)]
                          [:button.poll-modal__option
                           {:on-click #(on-answer-poll! (inc index))}
                           option]))
          login-to-answer [:button.poll-modal__option
                           {:on-click tunnus/login!}
                           (translate :poll :login-to-answer)]]
      [modal/component {:class    [:poll-modal :poll-modal--answering (when (= "slider" (:poll-type poll))
                                                                        :poll-modal--slider)]
                        :on-close #(close-poll-modal-and-track-event!)}
       [:h1.poll-modal__header
        title]
       (if @state/logged-in
         (cond
           @loading [:div [icons/spinner {:class :loading-indicator}]]
           (= "slider" (:poll-type poll)) (let [{:keys [emoji left-text right-text]} (:slider-options poll)]
                                            [:<>
                                             [:div.poll-modal__slider
                                              [:div.poll-modal__slider__texts
                                               [:span left-text]
                                               [:span right-text]]
                                              [:> slider
                                               (merge
                                                slider-common-props
                                                {:value      @slider-value
                                                 :on-change  #(reset! slider-value %)
                                                 :handle     (slider-handle emoji)})]]
                                             [:button.poll-modal__slider__submit
                                              {:on-click #(on-answer-poll! @slider-value)}
                                              icons/check]])
           :else options)
         login-to-answer)])))

(defn plurality [count] (if (= count 1) :singular :plural))

(defn poll-results [results]
  (let [close-modal! #(close-poll-modal!)
        regular-poll? (= (:poll-type results) "poll")
        sum (if regular-poll?
              (util/sum (map :count (:poll-results results)))
              (get-in results [:poll-results :count]))
        get-width #(if (zero? sum)
                     0
                     (util/format-percentage (/ % sum)))]
    [modal/component {:class    [:poll-modal]
                      :on-close close-modal!}
     [:h1.poll-modal__results-header
      (if regular-poll?
        (translate :poll :results)
        (:title results))]
     [:h2.poll-modal__results-subheader
      (if regular-poll?
        (:title results)
        (translate :poll :slider-results))
      (gstring/format " %d %s." sum (translate :poll :votes (plurality sum)))]
     (if regular-poll?
       [:div.poll-modal__scroll-container
        (for [result (:poll-results results)]
          [:div.poll-result {:key (:option result)}
           [:div.poll-result__text
            [:span (:option result)]
            [:span (get-width (:count result))]]
           [:div.poll-result__bar
            [:div.poll-result__bar--value {:style {:width (get-width (:count result))}}]]])]
       [:<> ;; slider
        [:div.poll-modal__slider
         [:div.poll-modal__slider__texts
          [:span (-> results :poll-results :min-text)]
          [:span (-> results :poll-results :max-text)]]
         [:> slider (merge
                     slider-common-props
                     {:value (-> results :poll-results :average)
                      :disabled true
                      :handle (slider-handle (-> results :poll-results :emoji))})]]])
     [:button.poll-modal__option
      {:on-click close-modal!}
      (translate :close)]]))

(defn poll-thanks []
  (let [close-modal! #(close-poll-modal!)]
    [modal/component {:class    [:poll-modal :poll-modal--answered]
                      :on-close close-modal!}
     [:h1.poll-modal__header
      (translate :poll :thanks-header)]
     [:div
      [:button.poll-modal__option
       {:on-click close-modal!}
       (translate :close)]]]))

(defn poll-modal []
  (let [{:keys [poll results step]} @poll-state]
    (case step
      :show-poll [poll-answering poll]
      :show-poll-results [poll-results results]
      :show-thanks [poll-thanks]
      nil)))
